import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogIn, LogOut, Globe, ChevronDown, ArrowLeft, Briefcase, Info, Award, ListChecks, ShieldCheck, Tag, ScrollText, Trophy, GraduationCap, BookOpen, Mail, Scale } from "lucide-react";
import { LANGS } from "../i18n";
import { useLang } from "../context/LanguageContext";
import { EXTRA } from "../content/extra";
import { CertificateTrigger, CertificateModal } from "./CertificateShowcase";

const NAV_ICONS = {
  services: Briefcase,
  about: Info,
  certification: Award,
  postup: ListChecks,
  standard: ShieldCheck,
  pricing: Tag,
  "o-certifikate": ScrollText,
  "hall-of-fame": Trophy,
  "program-saubereko": GraduationCap,
  "pre-investorov": Scale,
  metodika: BookOpen,
};

const Wordmark = () => (
  <Link
    to="/"
    data-testid="brand-logo"
    className="font-display text-2xl md:text-3xl font-semibold tracking-wide text-white"
  >
    Sauber<span className="gold-text">Eko</span>
    <sup className="text-[#D4AF37] text-xs ml-0.5">®</sup>
  </Link>
);

export const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goBack = () => {
    if (window.history.length > 2) navigate(-1);
    else navigate("/");
  };
  const backLabel = { sk: "Späť", en: "Back", de: "Zurück", pl: "Wstecz", fr: "Retour" }[lang] || "Späť";

  const hasToken = () => Boolean(
    localStorage.getItem("saubereko_applicant_token") || localStorage.getItem("saubereko_admin_token")
  );

  useEffect(() => { setAuthed(hasToken()); }, [location.pathname]);
  useEffect(() => {
    const sync = () => setAuthed(hasToken());
    window.addEventListener("storage", sync);
    window.addEventListener("saubereko-auth", sync);
    return () => { window.removeEventListener("storage", sync); window.removeEventListener("saubereko-auth", sync); };
  }, []);

  const doLogout = () => {
    localStorage.removeItem("saubereko_applicant_token");
    localStorage.removeItem("saubereko_admin_token");
    setAuthed(false);
    setOpen(false);
    navigate("/");
  };

  const myPortalLabel = { sk: "Môj portál", en: "My portal", de: "Mein Portal", pl: "Mój portal", fr: "Mon portail" }[lang] || "Môj portál";
  const logoutLabel = { sk: "Odhlásiť", en: "Log out", de: "Abmelden", pl: "Wyloguj", fr: "Déconnexion" }[lang] || "Odhlásiť";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const procLabel = { sk: "Postup", en: "Process", de: "Ablauf", pl: "Proces", fr: "Démarche" }[lang] || "Postup";
  const links = [
    { to: "/services", label: t.nav.services, id: "services" },
    { to: "/about", label: t.nav.about, id: "about" },
    { to: "/certification", label: t.nav.certification, id: "certification" },
    { to: "/postup", label: procLabel, id: "postup" },
    { to: "/standard", label: t.nav.standard, id: "standard" },
    { to: "/pricing", label: EXTRA[lang].nav.pricing, id: "pricing" },
    { to: "/o-certifikate", label: ({ sk: "O certifikáte", en: "About the certificate", de: "Über das Zertifikat", pl: "O certyfikacie", fr: "À propos du certificat" })[lang] || "O certifikáte", id: "o-certifikate" },
    { to: "/hall-of-fame", label: EXTRA[lang].hallOfFame.nav, id: "hall-of-fame" },
    { to: "/program-saubereko", label: ({ sk: "Pre firmy", en: "For companies", de: "Für Unternehmen", pl: "Dla firm", fr: "Pour les entreprises" })[lang] || "Pre firmy", id: "program-saubereko",
      hint: ({ sk: "Budujte svoj tím TOP profesionálov", en: "Build your team of TOP professionals", de: "Bauen Sie Ihr Team von TOP-Fachleuten auf", pl: "Zbuduj swój zespół TOP profesjonalistów", fr: "Constituez votre équipe de TOP professionnels" })[lang] },
    { to: "/pre-investorov", label: ({ sk: "Pre investorov", en: "For investors", de: "Für Investoren", pl: "Dla inwestorów", fr: "Pour les investisseurs" })[lang] || "Pre investorov", id: "pre-investorov",
      hint: ({ sk: "Záruka pravdy o nasadenom personáli", en: "A guarantee of truth about deployed staff", de: "Garantie der Wahrheit über eingesetztes Personal", pl: "Gwarancja prawdy o kierowanym personelu", fr: "La vérité garantie sur le personnel déployé" })[lang] },
    { to: "/metodika", label: ({ sk: "Pracuj, uč sa a zarábaj©", en: "Work, learn and earn©", de: "Arbeite, lerne und verdiene©", pl: "Pracuj, ucz się i zarabiaj©", fr: "Travaille, apprends et gagne©" })[lang] || "Pracuj, uč sa a zarábaj©", id: "metodika" },
  ];

  const primaryIds = new Set(["about"]);
  const primaryLinks = links.filter((l) => primaryIds.has(l.id));
  const moreLinks = links.filter((l) => !primaryIds.has(l.id));
  const moreLabel = { sk: "Viac", en: "More", de: "Mehr", pl: "Więcej", fr: "Plus" }[lang] || "Viac";
  const goContact = () => {
    setOpen(false);
    if (location.pathname === "/") {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#contact");
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || location.pathname !== "/"
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {location.pathname !== "/" && (
            <button
              data-testid="nav-back"
              onClick={goBack}
              aria-label={backLabel}
              className="flex items-center gap-1 border border-white/15 rounded-full pl-2 pr-2.5 sm:pr-3 py-1.5 text-xs tracking-wider text-white/75 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300 shrink-0"
            >
              <ArrowLeft size={15} /> <span className="hidden sm:inline">{backLabel}</span>
            </button>
          )}
          <Wordmark />
        </div>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-5">
          {primaryLinks.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              onClick={() => setOpen(false)}
              className={`text-sm tracking-wide whitespace-nowrap transition-colors duration-300 hover:text-[#D4AF37] ${
                location.pathname === l.to ? "text-[#D4AF37]" : "text-white/70"
              }`}
            >
              {l.label}
            </Link>
          ))}

          <div className="relative group" data-testid="nav-more">
            <button
              className={`flex items-center gap-1 text-sm tracking-wide whitespace-nowrap transition-colors duration-300 hover:text-[#D4AF37] ${
                moreLinks.some((l) => l.to === location.pathname) ? "text-[#D4AF37]" : "text-white/70"
              }`}
            >
              {moreLabel} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 hidden group-hover:block">
              <div className="flex flex-col bg-black/95 backdrop-blur-xl border border-white/15 rounded-lg overflow-hidden min-w-[200px] shadow-xl py-1">
                {moreLinks.map((l) => (
                  <Link
                    key={l.id}
                    to={l.to}
                    data-testid={`nav-link-${l.id}`}
                    onClick={() => setOpen(false)}
                    title={l.hint || undefined}
                    className={`group/item px-4 py-2.5 text-sm tracking-wide transition-colors duration-200 ${
                      location.pathname === l.to ? "bg-[#D4AF37] text-black font-semibold" : "text-white/75 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {l.label}
                    {l.hint && (
                      <span data-testid={`nav-hint-${l.id}`}
                        className={`block overflow-hidden max-h-0 opacity-0 group-hover/item:max-h-10 group-hover/item:opacity-100 group-hover/item:mt-1 transition-[max-height,opacity,margin] duration-300 text-xs font-normal tracking-normal ${location.pathname === l.to ? "text-black/70" : "text-[#D4AF37]"}`}>
                        {l.hint}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <button
            data-testid="nav-link-contact"
            onClick={goContact}
            className="text-sm tracking-wide whitespace-nowrap text-white/70 hover:text-[#D4AF37] transition-colors duration-300"
          >
            {t.nav.contact}
          </button>
        </nav>

        <div className="flex items-center gap-4">
          <div className="hidden xl:block">
            <CertificateTrigger onClick={() => setCertOpen(true)} />
          </div>

          <div className="relative" data-testid="lang-switcher">
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 border border-white/15 rounded-full px-2.5 sm:px-3 py-1.5 text-xs tracking-wider text-white/80 hover:text-white transition-colors duration-300"
              aria-label="Language"
              data-testid="lang-switcher-btn"
            >
              <Globe size={14} /> {(LANGS.find((l) => l.code === lang) || LANGS[0]).label}
              <ChevronDown size={12} className={`transition-transform duration-300 ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <>
                <div className="fixed inset-0 z-[40]" onClick={() => setLangOpen(false)} />
                <div className="absolute right-0 top-full pt-2 z-[50]">
                  <div className="flex flex-col bg-black/95 backdrop-blur-xl border border-white/15 rounded-lg overflow-hidden min-w-[120px] shadow-xl">
                    {LANGS.map((l) => (
                      <button
                        key={l.code}
                        data-testid={`lang-${l.code}`}
                        onClick={() => { setLang(l.code); setLangOpen(false); }}
                        className={`px-4 py-2.5 text-xs tracking-wider text-left transition-colors duration-200 ${
                          lang === l.code
                            ? "bg-[#D4AF37] text-black font-semibold"
                            : "text-white/70 hover:text-white hover:bg-white/5"
                        }`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {authed ? (
            <>
              <button
                data-testid="nav-my-portal"
                onClick={() => navigate("/ziadost")}
                className="hidden xl:inline-flex items-center gap-1.5 text-sm tracking-wide whitespace-nowrap text-white/80 hover:text-[#D4AF37] transition-colors duration-300"
              >
                <LogIn size={16} /> {myPortalLabel}
              </button>
              <button
                data-testid="nav-logout"
                onClick={doLogout}
                className="hidden xl:inline-flex items-center gap-1.5 bg-[#D4AF37] text-black text-sm font-semibold whitespace-nowrap px-6 py-2.5 hover:bg-[#F2E3C6] transition-colors duration-300"
              >
                <LogOut size={16} /> {logoutLabel}
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="nav-login"
                onClick={() => navigate("/ziadost")}
                className="hidden xl:inline-flex items-center gap-1.5 text-sm tracking-wide whitespace-nowrap text-white/80 hover:text-[#D4AF37] transition-colors duration-300"
              >
                <LogIn size={16} /> {t.nav.login}
              </button>

              <button
                data-testid="nav-cta"
                onClick={() => navigate("/ziadost")}
                className="hidden xl:inline-block bg-[#D4AF37] text-black text-sm font-semibold whitespace-nowrap px-6 py-2.5 hover:bg-[#F2E3C6] transition-colors duration-300"
              >
                {t.nav.cta}
              </button>
            </>
          )}

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden overflow-y-auto overscroll-contain backdrop-blur-xl bg-black/95 border-t border-white/10"
            style={{ maxHeight: "calc(100dvh - 64px)" }}
          >
            <div className="px-6 py-6 pb-16 flex flex-col gap-5">
              <CertificateTrigger compact onClick={() => { setOpen(false); setCertOpen(true); }} />
              {links.map((l) => {
                const Ic = NAV_ICONS[l.id];
                return (
                  <Link
                    key={l.id}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 text-left font-display text-2xl text-white/80 hover:text-[#D4AF37]"
                  >
                    {Ic && <Ic size={22} className="text-[#D4AF37] shrink-0" />}
                    <span>
                      {l.label}
                      {l.hint && <span className="block font-sans text-xs text-[#D4AF37]/80 mt-0.5">{l.hint}</span>}
                    </span>
                  </Link>
                );
              })}
              <button
                onClick={goContact}
                className="flex items-center gap-3 text-left font-display text-2xl text-white/80 hover:text-[#D4AF37]"
              >
                <Mail size={22} className="text-[#D4AF37] shrink-0" /> {t.nav.contact}
              </button>
              {authed ? (
                <>
                  <Link
                    to="/ziadost"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-my-portal"
                    className="flex items-center gap-2 text-left font-display text-2xl text-white/80 hover:text-[#D4AF37]"
                  >
                    <LogIn size={22} /> {myPortalLabel}
                  </Link>
                  <button
                    onClick={doLogout}
                    data-testid="mobile-nav-logout"
                    className="flex items-center gap-2 text-left font-display text-2xl text-[#D4AF37] hover:text-[#F2E3C6]"
                  >
                    <LogOut size={22} /> {logoutLabel}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/ziadost"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-login"
                    className="flex items-center gap-2 text-left font-display text-2xl text-white/80 hover:text-[#D4AF37]"
                  >
                    <LogIn size={22} /> {t.nav.login}
                  </Link>
                  <Link
                    to="/ziadost"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-cta"
                    className="text-left font-display text-2xl text-[#D4AF37] hover:text-[#F2E3C6]"
                  >
                    {t.nav.cta}
                  </Link>
                </>
              )}
              <div className="flex items-center gap-2 pt-2">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`px-4 py-2 text-sm border ${
                      lang === l.code
                        ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                        : "text-white/70 border-white/20"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CertificateModal open={certOpen} onClose={() => setCertOpen(false)} />
    </header>
  );
};
